import { useState, useEffect } from 'react';

/**
 * Custom hook for persisting state to localStorage
 * @param {string} key - localStorage key
 * @param {*} initialValue - Default value if key doesn't exist
 * @returns {Array} [value, setValue] tuple
 */
export function useLocalStorage(key, initialValue) {
  // State to store our value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}

/**
 * Hook for storing historical time-series data with automatic pruning
 * @param {string} key - localStorage key
 * @param {number} maxAge - Maximum age in milliseconds (default: 30 days)
 * @returns {Object} { data, addDataPoint, clearOldData }
 */
export function useHistoricalData(key, maxAge = 30 * 24 * 60 * 60 * 1000) {
  const [data, setData] = useLocalStorage(key, []);

  // Add a new data point
  const addDataPoint = (value) => {
    const newPoint = {
      value,
      timestamp: Date.now()
    };

    setData(prevData => {
      const updatedData = [...prevData, newPoint];
      // Keep only data within maxAge
      const cutoff = Date.now() - maxAge;
      return updatedData.filter(point => point.timestamp > cutoff);
    });
  };

  // Clear data older than maxAge
  const clearOldData = () => {
    const cutoff = Date.now() - maxAge;
    setData(prevData => prevData.filter(point => point.timestamp > cutoff));
  };

  // Auto-cleanup on mount
  useEffect(() => {
    clearOldData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, addDataPoint, clearOldData };
}

/**
 * Hook for managing user preferences/settings
 * @returns {Object} { preferences, updatePreference, resetPreferences }
 */
export function usePreferences() {
  const defaultPreferences = {
    refreshInterval: 60000, // 1 minute - optimized for API rate limits
    stalenessThreshold: 5, // 5 minutes - when to show stale data warning
    notifications: {
      enabled: true,
      email: '',
      slack: '',
      discord: ''
    },
    thresholds: {
      utilization: { yellow: 80, red: 90 },
      pegDeviation: { yellow: 0.5, red: 1 },
      loopExposure: { yellow: 30, red: 40 },
      fundingRate: { yellow: -0.5, red: -1 },
      tvlChange: { yellow: 0, red: -1 }
    },
    portfolio: {
      exposurePercent: 5,
      vaultCount: 3,
      totalValue: 100000
    }
  };

  const [preferences, setPreferences] = useLocalStorage('user-preferences', defaultPreferences);

  const updatePreference = (path, value) => {
    setPreferences(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  return { preferences, updatePreference, resetPreferences };
}

